import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["type", "spacingToEdge", "offsetToTarget", "maxWidth", "placement", "trigger", "classes", "size", "popperModifiers"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState } from 'react';
import classnames from 'classnames';
import { ClickAwayListener, Tooltip } from '@mui/material';
import { isTouchDevices } from '../utils/platformUtils';
import { createTooltipTheme } from './tooltipStyles';
import { EmbersInnerThemeProvider, useRtl, transformPlacement } from '../rtlProvider';

// wanna change it to 'info' and 'error' in the future, and use size to distinct the spacing

var offsetNum = 17;
export var EmbersTooltip = function EmbersTooltip(props) {
  var isTouchDevice = isTouchDevices();
  var _useRtl = useRtl(),
    isRTL = _useRtl.isRTL,
    transformRTL = _useRtl.transformRTL;
  var _props$type = props.type,
    type = _props$type === void 0 ? 'appName' : _props$type,
    _props$spacingToEdge = props.spacingToEdge,
    spacingToEdge = _props$spacingToEdge === void 0 ? 24 : _props$spacingToEdge,
    _props$offsetToTarget = props.offsetToTarget,
    offsetToTarget = _props$offsetToTarget === void 0 ? isTouchDevice ? -28 : -6 : _props$offsetToTarget,
    _props$maxWidth = props.maxWidth,
    maxWidth = _props$maxWidth === void 0 ? isTouchDevice ? "calc(100vw - ".concat(spacingToEdge * 2, "px)") : 300 : _props$maxWidth,
    placement = props.placement,
    _props$trigger = props.trigger,
    trigger = _props$trigger === void 0 ? isTouchDevice ? 'click' : 'hover' : _props$trigger,
    classes = props.classes,
    size = props.size,
    popperModifiers = props.popperModifiers,
    otherProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    visible = _useState2[0],
    setVisible = _useState2[1];
  var _transformPlacement = function _transformPlacement() {
    var _placement;
    if (!placement) {
      _placement = isTouchDevice ? transformRTL('bottom-end', 'bottom-start') : type === 'appName' ? transformRTL('right', 'left') : transformRTL('right-start', 'left-start');
    } else {
      _placement = transformPlacement(isRTL, placement);
    }
    return _placement;
  };
  var embersSize;
  if (type === 'appName') {
    embersSize = 'small';
  } else {
    embersSize = size || 'large';
  }
  var triggerProps = {};
  if (trigger === 'click') {
    triggerProps = {
      open: visible,
      disableFocusListener: isTouchDevice,
      disableHoverListener: true,
      disableTouchListener: true,
      onClose: function onClose() {
        return setVisible(false);
      },
      TransitionProps: {
        timeout: {
          // fix popper will flash on ipad 15.4, it seems to be MUI's bug, fix that by change enter to 0.
          enter: 0,
          exit: 100
        }
      }
    };
  }
  var ToolTipElement = /*#__PURE__*/React.createElement(Tooltip, _extends({
    tabIndex: 0,
    classes: _objectSpread(_objectSpread({}, classes), {}, {
      popper: classnames('EmbersTooltip-root', classes === null || classes === void 0 ? void 0 : classes.popper),
      tooltip: "EmbersTooltip-".concat(embersSize)
    })
  }, triggerProps, {
    PopperProps: {
      popperOptions: {
        modifiers: [{
          name: 'offset',
          options: {
            offset: function offset(_ref) {
              var placement = _ref.placement,
                reference = _ref.reference;
              switch (placement) {
                case 'top-start':
                  return [reference.width / 2 - offsetNum, offsetToTarget];
                case 'top-end':
                  return [-reference.width / 2 + offsetNum - 3, offsetToTarget];
                case 'bottom-start':
                  return [reference.width / 2 - offsetNum + 3, offsetToTarget];
                case 'bottom-end':
                  return [-reference.width / 2 + offsetNum, offsetToTarget];
                case 'left-start':
                  return [reference.height / 2 - offsetNum + 2, offsetToTarget];
                case 'left-end':
                  return [reference.height / 2 - offsetNum - 5, offsetToTarget];
                case 'right-start':
                  return [reference.height / 2 - offsetNum + 2, offsetToTarget];
                case 'right-end':
                  return [reference.height / 2 - offsetNum - 5, offsetToTarget];
                default:
                  return [0, offsetToTarget];
              }
            }
          }
        }, {
          name: 'preventOverflow',
          options: {
            padding: spacingToEdge
          }
        }].concat(_toConsumableArray(popperModifiers || []))
      }
    },
    arrow: true,
    placement: _transformPlacement()
  }, otherProps), otherProps.children);
  var onClick = function onClick(e) {
    setVisible(true);
  };
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: createTooltipTheme(type, maxWidth)
  }, trigger === 'click' ? /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: function onClickAway() {
      return setVisible(false);
    }
  }, /*#__PURE__*/React.createElement("span", {
    onClick: onClick
  }, ToolTipElement)) : ToolTipElement);
};